import { withStyles } from '@material-ui/core/styles';
import AccessTime from '@material-ui/icons/AccessTime';
import Cached from '@material-ui/icons/Cached';
import Cancel from '@material-ui/icons/Cancel';
import Check from '@material-ui/icons/Check';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import GetAppIcon from '@material-ui/icons/GetApp';
import NotInterested from '@material-ui/icons/NotInterested';
import VisibilityIcon from '@material-ui/icons/Visibility';
import React from 'react';
import { Button, FunctionField, TextField } from 'react-admin';
import StatusChip from '../../shared/status-chip';
import utils from '../../shared/utils';
import TimezoneAwareDateField from '../../components/custom-fields/TimezoneAwareDateField';
import CurrencyField from '../../components/custom-fields/CurrencyField';
import { getBusinessConf } from '../config';

export const statusInfos = {
  PAID: { color: 'colorGreen', icon: <Check /> },
  PAID_EXTERNALLY: { color: 'colorGreen', icon: <Check /> },
  PAYMENT_IN_PROGRESS: { color: 'colorPurple', icon: <AccessTime /> },
  REFUND_IN_PROGRESS: { color: 'colorPurple', icon: <AccessTime /> },
  PENDING: { color: 'colorBlue', icon: <AccessTime /> },
  PENDING_EXTERNAL_PAYMENT: { color: 'colorBlue', icon: <AccessTime /> },
  PENDING_MANUAL_PAYMENT: { color: 'colorBlue', icon: <AccessTime /> },
  PENDING_AUTHENTICATION: { color: 'colorBlue', icon: <AccessTime /> },
  REFUSED: { color: 'colorRed', icon: <NotInterested /> },
  ERROR: { color: 'colorRed', icon: <ErrorOutline /> },
  REFUNDED: { color: 'colorYellow', icon: <Cached /> },
  CANCELLED: { color: 'colorRed', icon: <Cancel /> },
};

const CustomButton = withStyles({
  root: {
    padding: '6px 8px',
  },
})(({ classes, id, label, onClick, icon }) => (
  <Button className={classes.root} id={id} variant="outlined" label={label} size="small" onClick={onClick}>
    {icon}
  </Button>
));

export const InvoicesFields = ({
  translate,
  downloadPdf,
  downloadCSV,
  openSetAsPaidDialog,
  openCancelDialog,
  openRefundDialog,
  openRefundNote,
  downloadCreditNote,
  permissions,
  checkPermission,
  setInvoiceToPending,
}) => {
  const role = utils.getRoles();
  const { config } = getBusinessConf();
  return [
    <TimezoneAwareDateField key="invoiceDate" source="invoiceDate" sortable />,
    <CurrencyField key="total" source="amount" sortBy="amount" sortable label="resources.invoice.fields.total" />,
    <FunctionField
      key="pspReference"
      label="resources.invoice.fields.pspRef"
      render={ref => <span>{ref.pspReference ? ref.pspReference : '-'}</span>}
      sortable={false}
    />,
    <TextField key="type" source="type" label="resources.invoice.fields.type" sortable />,
    <FunctionField
      key="status"
      label="resources.invoice.fields.status"
      render={ref => {
        const status = ref && ref.invoicesStatus;
        if (status) {
          return ref.paidExternally || (ref.pspName && ref.pspName === 'ext_wire_transfer') ? (
            <StatusChip label={translate(`resources.invoice.status.PAID_EXTERNALLY`)} {...statusInfos[status]} />
          ) : (
            <StatusChip label={translate(`resources.invoice.status.${status}`)} {...statusInfos[status]} />
          );
        }
        return null;
      }}
      sortable
    />,
    <FunctionField
      key="downloadButton"
      label="resources.invoice.fields.actions"
      render={ref => (
        <div
          style={{
            display: 'flex',
            gap: '8px',
          }}
        >
          {ref && (ref.invoicesStatus === 'ERROR' || ref.invoicesStatus === 'REFUSED') && permissions === 'admin' && (
            <Button
              variant="outlined"
              label="resources.invoice.retryPayment"
              size="small"
              onClick={() => setInvoiceToPending(ref.id)}
            />
          )}
          {ref &&
            ref.invoicesStatus &&
            (ref.invoicesStatus === 'REFUSED' ||
              ref.invoicesStatus === 'PENDING_EXTERNAL_PAYMENT' ||
              ref.invoicesStatus === 'PENDING' ||
              ref.invoicesStatus === 'ERROR') &&
            permissions === 'admin' && (
              <Button
                id={`Selenium-Business-Invoices-list-markAsPaid-${ref.id}-Button`}
                variant="outlined"
                label="resources.invoice.setAsPaid"
                size="small"
                onClick={() => openSetAsPaidDialog(ref.id)}
              />
            )}
          {ref &&
            ref.invoicesStatus === 'PAID' &&
            (ref.pspName === 'ADYEN' || ref.pspName === 'STRIPE') &&
            ref.amount > 0 &&
            permissions === 'admin' && (
              <Button
                id={`Selenium-Business-Invoices-list-refund-${ref.id}-Button`}
                variant="outlined"
                label="resources.invoice.refund"
                size="small"
                onClick={() => openRefundDialog(ref)}
              />
            )}
          {ref &&
            (ref.invoicesStatus === 'ERROR' ||
              ref.invoicesStatus === 'REFUSED' ||
              ref.invoicesStatus === 'PENDING' ||
              ref.invoicesStatus === 'PENDING_AUTHENTICATION' ||
              ref.invoicesStatus === 'PENDING_EXTERNAL_PAYMENT') &&
            permissions === 'admin' && (
              <Button
                variant="outlined"
                label="resources.invoice.cancel"
                size="small"
                onClick={() => openCancelDialog(ref)}
              />
            )}
          {ref &&
            ref.invoicesStatus &&
            ref.invoicesStatus === 'REFUNDED' &&
            (permissions === 'admin' || role === 'owner') && (
              <Button
                id={`Selenium-Business-Invoices-list-refund-note-${ref.id}-Button`}
                variant="outlined"
                label="resources.invoice.refundNote"
                size="small"
                onClick={() => openRefundNote(ref.id)}
              >
                <VisibilityIcon />
              </Button>
            )}
          {ref &&
            ref.invoicesStatus === 'CANCELLED' &&
            ref.cancellationDate &&
            (permissions === 'admin' || role === 'owner') && (
              <Button
                id={`Selenium-Business-Invoices-list-credit-note-${ref.id}-Button`}
                variant="outlined"
                label="resources.invoice.creditNote"
                size="small"
                onClick={() => downloadCreditNote(ref.id)}
              />
            )}
          {checkPermission('VIEW_INVOICE_DOWNLOAD_OPTION') && ref.invoicesStatus !== 'CANCELLED' && (
            <CustomButton
              id={`Selenium-Business-Invoices-list-downloadButton-${ref.id}-Button-PDF`}
              label="resources.invoice.PDF"
              onClick={() => downloadPdf(ref.id)}
              icon={<GetAppIcon />}
            />
          )}
          {checkPermission('VIEW_INVOICE_DOWNLOAD_OPTION') &&
            ref.invoicesStatus !== 'CANCELLED' &&
            config &&
            config.showCSVDownloadInvoiceButton && (
              <CustomButton
                id={`Selenium-Business-Invoices-list-downloadButton-${ref.id}-Button-CSV`}
                label="resources.invoice.CSV"
                onClick={() =>
                  downloadCSV({
                    invoiceId: ref.id,
                    invoiceType: ref.type,
                    currency: ref.currency,
                    invoiceDate: ref.invoiceDate,
                  })
                }
                icon={<GetAppIcon />}
              />
            )}
        </div>
      )}
      sortable={false}
    />,
    <TextField key="id" source="id" sortable={false} />,
    <TextField key="sequence" source="sequence" sortable={false} />,
    <TextField key="fleetId" source="fleetId" sortable={false} />,
    <TimezoneAwareDateField key="invoiceYear" source="invoiceYear" sortable format="YYYY" />,
    <TextField key="entityId" source="entityId" sortable={false} />,
    <TextField key="attempts" source="attempts" sortable={false} />,
    <TextField key="pspName" source="pspName" sortable={false} />,
    <TextField key="tripExpensesCumulativeId" source="tripExpensesCumulativeId" sortable={false} />,
  ];
};
